import type {
  BookingDetail,
  BookingShipmentActivity,
  BookingShipmentGroupedV2,
  ShipmentActivityFormPayload,
  ShipmentActivityPayload,
  ShipmentProofFormPayload,
} from '@dc-trans/data-layer';
import { usePreview } from '@/context/previewContext';
import { arrivalClaimSchema, ProofDeliveryConditions, shipmentProofSchema } from '@dc-trans/data-layer';
import { useMemo, useState } from 'react';
import { TruckIcon } from '@/assets';
import { Col, Descriptions, Row } from 'antd';
import ExpandableContainer from '@/components/ui/ExpandableContainer';
import { Button, Card, Divider, Table } from 'pink-lava-ui';
import { PlusIcon } from 'lucide-react';
import { FormModal } from '@/components/ui/modal';
import { useNotification } from '@/context/notificationContext';
import dayjs from 'dayjs';
import queryClient from '@/libs/queryClient';
import { usePostCreateShipmentActivity } from '@/mutations';
import { useBookingShipmentGroupedV2 } from '@/queries';
import type { ApiErrorContext } from '@/context/apiErrorNotificationProvider';
import { groupBy } from 'es-toolkit';

const ShipmentListSection = ({
  booking,
  activityList,
}: {
  booking: BookingDetail;
  activityList: BookingShipmentActivity[];
}) => {
  const [selectedShipment, setSelectedShipment] = useState<string | undefined>();
  const [formVisible, setFormVisible] = useState(false);
  const [shipmentProofFormVisible, setShipmentProofFormVisible] = useState(false);
  const notification = useNotification();
  const preview = usePreview();
  const createShipmentActivityMutation = usePostCreateShipmentActivity({
    meta: {
      message: ({ variables }: ApiErrorContext) => {
        const payload = (variables as { payload?: ShipmentActivityPayload } | undefined)?.payload;
        if (payload?.activity_type === 'arrival_claim') {
          return 'Gagal menambahkan data arrival claim.';
        }
        if (payload?.activity_type === 'proof_of_delivery') {
          return 'Gagal menambahkan data Bukti Penerimaan.';
        }
        return 'Gagal menambahkan data aktivitas pengiriman.';
      },
    },
  });

  const handleSubmit =
    (formType: 'arrival_claim' | 'proof_of_delivery') =>
    async (values: ShipmentProofFormPayload | ShipmentActivityFormPayload) => {
      const payload: ShipmentActivityPayload = {
        ...values,
        activity_type: formType,
      };

      // Use the mutation as a promise to make it throw errors
      await createShipmentActivityMutation.mutateAsync({
        id: booking.ulid,
        payload,
      });

      // If successful, invalidate queries and show notification
      queryClient.invalidateQueries({
        queryKey: formType === 'arrival_claim' ? ['booking', booking.ulid] : ['booking'],
      });
      notification.success({
        message: `Berhasil menambahkan data ${formType === 'arrival_claim' ? 'arrival claim' : 'Bukti Penerimaan'}`,
      });
      setFormVisible(false);
      setShipmentProofFormVisible(false);
    };

  const bookingShipmentGroupedQueryV2 = useBookingShipmentGroupedV2(booking.ulid!, {
    page: 1,
    limit: 100,
  });

  const bookingShipmentGroupedList = useMemo(
    () => bookingShipmentGroupedQueryV2.data?.data?.items || [],
    [bookingShipmentGroupedQueryV2]
  );

  const arrivalClaimAttachmentMap = useMemo(() => {
    if (!activityList || !bookingShipmentGroupedList) return {};
    return activityList
      .filter(item =>
        bookingShipmentGroupedList.some(
          shipment => shipment.destination_ulid === item.destination_ulid && item.activity_type === 'arrival_claim'
        )
      )
      .reduce(
        (acc, item) => {
          acc[item.destination_ulid!] = {
            urls: item.attachments.map(img => img.url),
            description: item.notes,
            time: item.activity_time,
          };
          return acc;
        },
        {} as Record<string, { urls: string[]; description: string; time: string }>
      );
  }, [bookingShipmentGroupedList, activityList]);

  const proofOfDeliveryAttachmentMap = useMemo(() => {
    if (!activityList || !bookingShipmentGroupedList) return {};
    return activityList
      .filter(item =>
        bookingShipmentGroupedList.some(
          shipment => shipment.destination_ulid === item.destination_ulid && item.activity_type === 'proof_of_delivery'
        )
      )
      .reduce(
        (acc, item) => {
          acc[item.destination_ulid!] = {
            urls: item.attachments.map(img => img.url),
            description: item.notes,
            conditions: item.additional_data?.conditions || [],
            time: item.activity_time,
          };
          return acc;
        },
        {} as Record<
          string,
          {
            urls: string[];
            description: string;
            time: string;
            conditions: string[];
          }
        >
      );
  }, [bookingShipmentGroupedList, activityList]);

  return (
    <div className="flex flex-col gap-4">
      {bookingShipmentGroupedList.map(item => {
        return (
          <Card size="small" rounded="large" key={`${item.destination_ulid}-${item.origin_ulid}`}>
            <div className="flex gap-4 flex-col">
              <div className="flex gap-2 items-center">
                <img src={TruckIcon} className="w-5 h-5" />
                <p className="text-lg font-semibold">Shipments</p>
              </div>
              <Descriptions
                classNames={{
                  content:
                    'w-full flex items-end justify-end text-left md:items-start md:justify-start md:text-right font-nunito',
                  label: 'md:w-[240px] font-nunito',
                }}
                size="small"
                colon={false}
                column={1}
                items={[
                  {
                    label: 'Tujuan',
                    children: item.destination_name,
                  },
                  {
                    label: 'Nomor Shipment',
                    children: (() => {
                      const shipmentItem = item as BookingShipmentGroupedV2;
                      // Group DO numbers by shipment number using es-toolkit
                      const groupedByShipment = groupBy(shipmentItem.tms_do_pairs, item => item.tms_ref_number);

                      // Convert to array format for Table
                      const shipmentList = Object.entries(groupedByShipment)
                        .map(([tms_ref_number, items]) => ({
                          tms_ref_number,
                          do_numbers: items
                            .map(item => item.do_number)
                            .filter(item => item)
                            .sort((a, b) => Number(a) - Number(b))
                            .join(', '),
                        }))
                        .sort((a, b) => Number(a.tms_ref_number) - Number(b.tms_ref_number));

                      return (
                        <ExpandableContainer
                          minimizedHeight={68}
                          buttonPlacement="right"
                          className="w-full md:text-left"
                        >
                          <Table
                            size="small"
                            dataSource={shipmentList}
                            scroll={{
                              x: 360,
                            }}
                            columns={[
                              {
                                title: 'Nomor Shipment',
                                dataIndex: 'tms_ref_number',
                                key: 'tms_ref_number',
                                minWidth: 130,
                                className: 'leading-[32px]! py-0!',
                              },
                              {
                                title: 'Nomor Delivery order',
                                dataIndex: 'do_numbers',
                                className: 'leading-[32px]! py-0!',
                                key: 'do_numbers',
                              },
                            ]}
                            rowKey="tms_ref_number"
                          />
                        </ExpandableContainer>
                      );
                    })(),
                  },
                  {
                    label: 'Dokumen Arrival Claim',
                    children:
                      ['arrival_claim', 'completed'].indexOf(item.status) !== -1 ? (
                        <Button
                          type="tertiary"
                          className="bg-white! px-2!"
                          size="small"
                          onClick={() => {
                            preview({
                              title: 'Dokumen Arrival Claim',
                              content: arrivalClaimAttachmentMap[item.destination_ulid!].urls,
                              fullscreen: true,
                              description: (
                                <Descriptions
                                  column={1}
                                  size="small"
                                  classNames={{
                                    content: 'font-nunito-bold text-white! text-shadow-lg',
                                    label: 'font-nunito-bold max-w-1/2 text-white! text-shadow-lg',
                                  }}
                                  items={[
                                    {
                                      label: 'Waktu Arrival Claim',
                                      children: arrivalClaimAttachmentMap[item.destination_ulid!].time
                                        ? dayjs(arrivalClaimAttachmentMap[item.destination_ulid!].time).format(
                                            'DD MMMM YYYY, HH:mm:ss'
                                          )
                                        : '-',
                                    },
                                    {
                                      label: 'Catatan',
                                      children: arrivalClaimAttachmentMap[item.destination_ulid!].description || '-',
                                    },
                                  ]}
                                />
                              ),
                            });
                          }}
                        >
                          Lihat Dokumen
                        </Button>
                      ) : (
                        '-'
                      ),
                  },
                  {
                    label: 'Dokumen Bukti Penerimaan',
                    children:
                      item.status === 'completed' ? (
                        <Button
                          type="tertiary"
                          className="bg-white! px-2!"
                          size="small"
                          onClick={() => {
                            preview({
                              title: 'Bukti Penerimaan',
                              content: proofOfDeliveryAttachmentMap[item.destination_ulid!].urls,
                              fullscreen: true,
                              description: (
                                <Descriptions
                                  column={1}
                                  size="small"
                                  classNames={{
                                    content: 'font-nunito-bold text-white! text-shadow-lg',
                                    label: 'font-nunito-bold max-w-1/2 text-white! text-shadow-lg',
                                  }}
                                  items={[
                                    {
                                      label: 'Waktu Bukti Penerimaan',
                                      children: proofOfDeliveryAttachmentMap[item.destination_ulid!].time
                                        ? dayjs(proofOfDeliveryAttachmentMap[item.destination_ulid!].time).format(
                                            'DD MMMM YYYY, HH:mm:ss'
                                          )
                                        : '-',
                                    },
                                    {
                                      label: 'Kondisi',
                                      children: Array.isArray(
                                        proofOfDeliveryAttachmentMap[item.destination_ulid!].conditions
                                      )
                                        ? proofOfDeliveryAttachmentMap[item.destination_ulid!].conditions
                                            .map(
                                              item =>
                                                ProofDeliveryConditions[item as keyof typeof ProofDeliveryConditions]
                                            )
                                            ?.join(', ')
                                        : '-',
                                    },
                                  ]}
                                />
                              ),
                            });
                          }}
                        >
                          Lihat Dokumen
                        </Button>
                      ) : (
                        '-'
                      ),
                  },
                ]}
              />
              {item.status !== 'completed' && <Divider size={1} className="my-0! border-gray-200!" />}
              <Row className="flex flex-row gap-2 items-end justify-end">
                {item.status !== 'completed' && (
                  <Col xs={24} md={{ flex: 'none' }}>
                    <Button
                      block
                      type="secondary"
                      size="small"
                      className="p-2! disabled:cursor-not-allowed!"
                      disabled={item.status !== 'arrival_claim' || !booking.assignment?.driver?.ulid}
                      onClick={() => {
                        setSelectedShipment(item.destination_ulid);
                        setShipmentProofFormVisible(true);
                      }}
                    >
                      <PlusIcon strokeWidth={3} size={16} />
                      Bukti Penerimaan
                    </Button>
                  </Col>
                )}
                {['arrival_claim', 'completed'].indexOf(item.status) === -1 && (
                  <Col xs={24} md={{ flex: 'none' }}>
                    <Button
                      block
                      type="primary"
                      size="small"
                      className="p-2!"
                      disabled={
                        booking.status !== 'in_progress' ||
                        !booking.assignment?.driver?.ulid ||
                        ['on_the_way', 'arrived_at_destination'].indexOf(item.status) === -1
                      }
                      onClick={() => {
                        setSelectedShipment(item.destination_ulid);
                        setFormVisible(true);
                      }}
                    >
                      <PlusIcon strokeWidth={3} size={16} />
                      Arrival Claim
                    </Button>
                  </Col>
                )}
              </Row>
            </div>
          </Card>
        );
      })}
      <FormModal<ShipmentActivityFormPayload>
        visible={formVisible}
        initialValues={{
          destination_ulid: selectedShipment,
          attachment_ulids: [],
        }}
        validationSchema={arrivalClaimSchema}
        mode="create"
        loading={createShipmentActivityMutation.isPending}
        title={'Arrival Claim'}
        fields={[
          {
            name: 'destination_ulid',
            label: 'Tujuan Pengantaran',
            placeholder: 'Pilih',
            disabled: true,
            type: 'select',
            options: (booking.routes || []).map(item => ({
              label: item.destination_name,
              value: item.destination_ulid,
            })),
          },
          {
            name: 'attachment_ulids',
            label: 'Bukti Arrival Claim (Maks. 3 file)',
            maxConcurrentUploads: 3,
            type: 'attachment',
            required: true,
          },
          {
            name: 'notes',
            label: 'Catatan Tambahan',
            type: 'text',
            placeholder: 'Cth: sudah diantar',
          },
        ]}
        onCancel={() => setFormVisible(false)}
        onSubmit={handleSubmit('arrival_claim')}
      />
      <FormModal<ShipmentProofFormPayload>
        visible={shipmentProofFormVisible}
        initialValues={{
          destination_ulid: selectedShipment,
          attachment_ulids: [],
          conditions: [],
        }}
        validationSchema={shipmentProofSchema}
        mode="create"
        loading={createShipmentActivityMutation.isPending}
        title={'Bukti Penerimaan'}
        fields={[
          {
            name: 'destination_ulid',
            label: 'Tujuan Pengantaran',
            placeholder: 'Pilih',
            disabled: true,
            type: 'select',
            options: (booking.routes || []).map(item => ({
              label: item.destination_name,
              value: item.destination_ulid,
            })),
          },
          {
            name: 'attachment_ulids',
            label: 'Bukti Penerimaan',
            maxConcurrentUploads: 3,
            type: 'attachment',
            required: true,
          },
          {
            name: 'conditions',
            label: 'Kondisi',
            options: Object.entries(ProofDeliveryConditions).map(([key, value]) => ({
              label: value,
              value: key,
            })),
            type: 'checkbox',
            required: true,
            mutuallyExclusiveValue: 'tidak_ada_kendala',
          },
        ]}
        onCancel={() => setShipmentProofFormVisible(false)}
        onSubmit={handleSubmit('proof_of_delivery')}
      />
    </div>
  );
};

export default ShipmentListSection;
